import React, { useState, useRef } from 'react';
import './styles.scss';
import { DismissRegular } from '@fluentui/react-icons';

interface EmailTagInputProps {
    value: string[];
    onChange: (emails: string[]) => void;
    placeholder?: string;
}

const EmailTagInput: React.FC<EmailTagInputProps> = ({ value, onChange, placeholder }) => {
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleContainerClick = () => {
        inputRef.current?.focus();
    };

    const addEmail = (email: string) => {
        const trimmedEmail = email.trim();
        if (trimmedEmail && !value.includes(trimmedEmail)) {
            onChange([...value, trimmedEmail]);
        }
    };

    const removeEmail = (emailToRemove: string) => {
        onChange(value.filter(email => email !== emailToRemove));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        
        if (newValue.endsWith(',') || newValue.endsWith(' ')) {
            const email = newValue.slice(0, -1);
            addEmail(email);
            setInputValue('');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (inputValue.trim()) {
                addEmail(inputValue);
                setInputValue('');
            }
        } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
            removeEmail(value[value.length - 1]);
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const emails = pastedText.split(/[,;\s]+/).filter(email => email.trim());
        
        if (emails.length > 0) {
            // Add all emails at once to avoid state update issues
            const newEmails = emails
                .map(email => email.trim())
                .filter(email => email && !value.includes(email));
                
            if (newEmails.length > 0) {
                onChange([...value, ...newEmails]);
            }
        }
    };

    const handleBlur = () => {
        if (inputValue.trim()) {
            addEmail(inputValue);
            setInputValue('');
        }
    };

    return (
        <div 
            ref={containerRef}
            className="email-tag-input"
            onClick={handleContainerClick}
        >
            
            <div className="email-tag-container">
                {value.map((email, index) => {
                    return (
                        <span key={index} className="email-tag" title={email}>
                            <span className="email-tag-text">{email}</span>
                            <span
                                className="email-tag-remove"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeEmail(email);
                                }}
                            >
                                <DismissRegular />
                            </span>
                        </span>
                    );
                })}
                <input
                    autoComplete="new-off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-form-type="other"
                    data-lpignore="true"
                    ref={inputRef}
                    type="text"
                    className="email-input"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onBlur={handleBlur}
                    placeholder={value.length === 0 ? placeholder : ''}
                />
            </div>
        </div>
    );
};

export default EmailTagInput;
